import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { ApiResponse, Notification, NotificationSummary } from '@core/types';

export interface NotificationGridResult {
  list: Notification[];
  totalRecord: number;
  pageCount: number;
}

export interface NotificationGridConfig {
  pageNumber: number;
  pageSize: number;
  status?: number;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.api}/notifications`;

  getSummary(): Observable<ApiResponse<NotificationSummary>> {
    return this.http.get<ApiResponse<NotificationSummary>>(`${this.apiUrl}/summary`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading notifications')
      )
    );
  }

  getGrid(config: NotificationGridConfig): Observable<ApiResponse<NotificationGridResult>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.status != null) {
      params = params.set('filter.status', config.status.toString());
    }

    return this.http.get<ApiResponse<NotificationGridResult>>(`${this.apiUrl}/grid`, { params }).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading notifications')
      )
    );
  }

  markAsRead(id: string): Observable<ApiResponse<void>> {
    return this.http.patch<ApiResponse<void>>(`${this.apiUrl}/${id}/read`, {}).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error marking notification as read')
      )
    );
  }

  markAllAsRead(): Observable<ApiResponse<void>> {
    return this.http.patch<ApiResponse<void>>(`${this.apiUrl}/read-all`, {}).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error marking all notifications as read')
      )
    );
  }
}
